import { useState } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, RotateCcw, Monitor, Gamepad2, DollarSign, Briefcase, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { gpuData, GPU } from '@/data/gpuData';
import { cn } from '@/lib/utils';

interface QuizAnswer {
  budget: string;
  resolution: string;
  games: string[];
  useCase: string;
}

const budgetOptions = [
  { id: 'budget', label: 'Budget', range: '$200 - $350', min: 200, max: 350, icon: '💰' },
  { id: 'midrange', label: 'Mid-Range', range: '$350 - $600', min: 350, max: 600, icon: '💵' },
  { id: 'highend', label: 'High-End', range: '$600 - $1000', min: 600, max: 1000, icon: '💎' },
  { id: 'enthusiast', label: 'Enthusiast', range: '$1000+', min: 1000, max: 9999, icon: '🚀' },
];

const resolutionOptions = [
  { id: '1080p', label: '1080p (Full HD)', desc: 'Great for high refresh rate gaming', icon: Monitor, size: 'w-4 h-4' },
  { id: '1440p', label: '1440p (QHD)', desc: 'Sweet spot for quality and performance', icon: Monitor, size: 'w-5 h-5' },
  { id: '4k', label: '4K (Ultra HD)', desc: 'Maximum visual fidelity', icon: Monitor, size: 'w-6 h-6' },
];

const gameOptions = [
  { id: 'esports', label: 'Esports / Competitive', examples: 'Valorant, CS2, Fortnite', icon: '🎯' },
  { id: 'aaa', label: 'AAA / Story Games', examples: 'Cyberpunk, RDR2, Hogwarts', icon: '🎮' },
  { id: 'openworld', label: 'Open World / RPG', examples: 'Elden Ring, Starfield', icon: '🗺️' },
  { id: 'simulation', label: 'Simulation / Strategy', examples: 'Flight Sim, Cities', icon: '✈️' },
];

const useCaseOptions = [
  { id: 'gaming', label: 'Gaming Only', desc: 'Focused on game performance', icon: Gamepad2 },
  { id: 'streaming', label: 'Gaming + Streaming', desc: 'Need encoder for streaming', icon: '📺' },
  { id: 'creative', label: 'Gaming + Creative', desc: 'Video editing, 3D rendering', icon: '🎨' },
  { id: 'workstation', label: 'Professional Work', desc: 'CAD, ML, professional apps', icon: Briefcase },
];

function getRecommendations(answers: QuizAnswer): GPU[] {
  const budget = budgetOptions.find(b => b.id === answers.budget);
  if (!budget) return [];

  let filtered = gpuData.filter(gpu => gpu.msrp >= budget.min && gpu.msrp <= budget.max);

  // Resolution-based performance requirements
  let minPerformance = 20;
  if (answers.resolution === '1440p') minPerformance = 35;
  if (answers.resolution === '4k') minPerformance = 55;

  // AAA games need more power
  if (answers.games.includes('aaa') || answers.games.includes('openworld')) {
    minPerformance += 10;
  }

  // Creative/workstation benefits from more VRAM (NVIDIA preferred for CUDA)
  if (answers.useCase === 'creative' || answers.useCase === 'workstation') {
    filtered = filtered.filter(gpu => {
      const vramGB = parseInt(gpu.vram.split('GB')[0]);
      return vramGB >= 8;
    });
  }

  // Streaming benefits from NVIDIA NVENC
  if (answers.useCase === 'streaming') {
    filtered = filtered.sort((a, b) => {
      if (a.brand === 'nvidia' && b.brand !== 'nvidia') return -1;
      if (b.brand === 'nvidia' && a.brand !== 'nvidia') return 1;
      return b.performance - a.performance;
    });
  }

  // Filter by minimum performance
  filtered = filtered.filter(gpu => gpu.performance >= minPerformance);

  // Sort by value (performance per dollar)
  filtered = filtered.sort((a, b) => {
    const valueA = a.performance / a.msrp;
    const valueB = b.performance / b.msrp;
    return valueB - valueA;
  });

  // Return top 3 recommendations
  return filtered.slice(0, 3);
}

export const GPUQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer>({
    budget: '',
    resolution: '',
    games: [],
    useCase: '',
  });
  const [showResults, setShowResults] = useState(false);

  const totalSteps = 4;
  const progress = ((step + 1) / totalSteps) * 100;

  const canProceed = () => {
    switch (step) {
      case 0: return answers.budget !== '';
      case 1: return answers.resolution !== '';
      case 2: return answers.games.length > 0;
      case 3: return answers.useCase !== '';
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleReset = () => {
    setStep(0);
    setAnswers({ budget: '', resolution: '', games: [], useCase: '' });
    setShowResults(false);
  };

  const toggleGame = (gameId: string) => {
    setAnswers(prev => ({
      ...prev,
      games: prev.games.includes(gameId)
        ? prev.games.filter(g => g !== gameId)
        : [...prev.games, gameId],
    }));
  };

  const recommendations = showResults ? getRecommendations(answers) : [];

  const brandColors = {
    nvidia: 'border-nvidia bg-nvidia/10',
    amd: 'border-amd bg-amd/10',
    intel: 'border-intel bg-intel/10',
  };

  const brandTextColors = {
    nvidia: 'text-nvidia',
    amd: 'text-amd',
    intel: 'text-intel',
  };

  if (showResults) {
    return (
      <section id="quiz" className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Your Recommendations</span>
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Best GPUs For You
              </h2>
              <p className="text-muted-foreground">
                Based on your {budgetOptions.find(b => b.id === answers.budget)?.range} budget, 
                {' '}{answers.resolution} gaming preferences
              </p>
            </div>

            {recommendations.length > 0 ? (
              <div className="space-y-4 mb-8">
                {recommendations.map((gpu, index) => (
                  <Card
                    key={gpu.id}
                    className={cn(
                      'p-6 border-2 transition-all hover:shadow-lg',
                      brandColors[gpu.brand],
                      index === 0 && 'ring-2 ring-primary/50'
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {index === 0 && (
                            <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                              Best Match
                            </span>
                          )}
                          <span className={cn('text-xs font-semibold uppercase', brandTextColors[gpu.brand])}>
                            {gpu.brand}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-1">{gpu.name}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <span>{gpu.vram}</span>
                          <span>•</span>
                          <span>{gpu.architecture}</span>
                          <span>•</span>
                          <span>{gpu.tdp}W TDP</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-foreground">${gpu.msrp}</div>
                        <div className={cn('text-lg font-semibold', brandTextColors[gpu.brand])}>
                          {gpu.performance}% perf
                        </div>
                      </div>
                    </div>
                    
                    {/* Performance bar */}
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>Relative Performance</span>
                        <span>{gpu.performance}% vs RTX 4090</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            gpu.brand === 'nvidia' && 'bg-nvidia',
                            gpu.brand === 'amd' && 'bg-amd',
                            gpu.brand === 'intel' && 'bg-intel'
                          )}
                          style={{ width: `${Math.min(gpu.performance, 100)}%` }}
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center mb-8">
                <p className="text-muted-foreground">
                  No GPUs match your exact criteria. Try adjusting your budget or resolution requirements.
                </p>
              </Card>
            )}

            <div className="flex justify-center">
              <Button onClick={handleReset} variant="outline" className="gap-2">
                <RotateCcw className="w-4 h-4" />
                Take Quiz Again
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quiz" className="py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">GPU Finder</span>
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Find Your Perfect GPU
            </h2>
            <p className="text-muted-foreground">
              Answer a few questions and we'll recommend the best graphics card for you
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Question {step + 1} of {totalSteps}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Questions */}
          <Card className="p-6 mb-6">
            {/* Step 0: Budget */}
            {step === 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">What's your budget?</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {budgetOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setAnswers({ ...answers, budget: option.id })}
                      className={cn(
                        'p-4 rounded-lg border-2 text-left transition-all hover:border-primary/50',
                        answers.budget === option.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{option.icon}</span>
                        <span className="font-semibold">{option.label}</span>
                        {answers.budget === option.id && (
                          <Check className="w-4 h-4 text-primary ml-auto" />
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">{option.range}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Resolution */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Monitor className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">What resolution will you play at?</h3>
                </div>
                <div className="space-y-3">
                  {resolutionOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setAnswers({ ...answers, resolution: option.id })}
                      className={cn(
                        'w-full p-4 rounded-lg border-2 text-left transition-all hover:border-primary/50 flex items-center gap-4',
                        answers.resolution === option.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      )}
                    >
                      <option.icon className={cn('text-muted-foreground', option.size)} />
                      <div className="flex-1">
                        <div className="font-semibold">{option.label}</div>
                        <div className="text-sm text-muted-foreground">{option.desc}</div>
                      </div>
                      {answers.resolution === option.id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Games */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Gamepad2 className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">What games will you play? (Select all that apply)</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {gameOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => toggleGame(option.id)}
                      className={cn(
                        'p-4 rounded-lg border-2 text-left transition-all hover:border-primary/50',
                        answers.games.includes(option.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{option.icon}</span>
                        <span className="font-semibold text-sm">{option.label}</span>
                        {answers.games.includes(option.id) && (
                          <Check className="w-4 h-4 text-primary ml-auto" />
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{option.examples}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Use Case */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">What's your primary use case?</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {useCaseOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setAnswers({ ...answers, useCase: option.id })}
                      className={cn(
                        'p-4 rounded-lg border-2 text-left transition-all hover:border-primary/50',
                        answers.useCase === option.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">
                          {typeof option.icon === 'string' ? option.icon : <option.icon className="w-5 h-5" />}
                        </span>
                        <span className="font-semibold text-sm">{option.label}</span>
                        {answers.useCase === option.id && (
                          <Check className="w-4 h-4 text-primary ml-auto" />
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{option.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="gap-2"
            >
              {step === totalSteps - 1 ? 'See Results' : 'Next'}
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
